import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Button, token, textStyles, Textarea, Dropdown, Flex, Box } from '@bynder/design-system';
import { IconClear, IconDownload, IconMore, IconSparkles, IconUpload } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { type TranscriptCue } from '@bynder-studio/render-core';
import { parseSrt, cuesToSrt } from './parseSrt';
import TranscriptCueItem from './TranscriptCueItem';

type TranscriptFormProps = {
    srtContent: string;
    isGenerating: boolean;
    frameRate: number;
    designName?: string;
    pageName?: string;
    onGenerate: () => void;
    onSrtChange: (newSrtContent: string) => void;
    isDisabled?: boolean;
};

const sanitizeFileNamePart = (value: string): string =>
    value
        .trim()
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_');

const TranscriptForm = ({
    srtContent,
    isGenerating,
    frameRate,
    designName = '',
    pageName = '',
    onGenerate,
    onSrtChange,
    isDisabled = false,
}: TranscriptFormProps) => {
    const { translate } = useTranslate();
    const [editingCueId, setEditingCueId] = useState<string | null>(null);

    const cues = useMemo<TranscriptCue[]>(
        () => (srtContent ? parseSrt(srtContent, frameRate) : []),
        [srtContent, frameRate],
    );

    const handleCueUpdate = useCallback(
        (updatedCue: TranscriptCue) => {
            const newCues = cues.map((c) => (c.id === updatedCue.id ? updatedCue : c));

            onSrtChange(cuesToSrt(newCues, frameRate));
        },
        [cues, frameRate, onSrtChange],
    );

    const handleCueDelete = useCallback(
        (id: string) => {
            const newCues = cues.filter((c) => c.id !== id);

            setEditingCueId((current) => (current === id ? null : current));
            onSrtChange(cuesToSrt(newCues, frameRate));
        },
        [cues, frameRate, onSrtChange],
    );

    const handleCueInsertAfter = useCallback(
        (afterId: string) => {
            const afterIndex = cues.findIndex((c) => c.id === afterId);

            if (afterIndex === -1) {
                return;
            }

            const anchor = cues[afterIndex];
            const newId = `srt-cue-new-${Date.now()}`;
            const newCue: TranscriptCue = {
                id: newId,
                startFrame: anchor.endFrame,
                endFrame: anchor.endFrame + Math.round(frameRate * 3),
                text: '',
            };
            const newCues = [...cues.slice(0, afterIndex + 1), newCue, ...cues.slice(afterIndex + 1)];

            onSrtChange(cuesToSrt(newCues, frameRate));
            setEditingCueId(newId);
        },
        [cues, frameRate, onSrtChange],
    );

    const handleClearAll = useCallback(() => {
        setEditingCueId(null);
        onSrtChange('');
    }, [onSrtChange]);

    const handleUpload = useCallback(() => {
        const input = document.createElement('input');

        input.type = 'file';
        input.accept = '.srt';

        input.onchange = () => {
            const file = input.files?.[0];

            if (!file) {
                return;
            }

            const reader = new FileReader();

            reader.onload = (e) => {
                const content = e.target?.result;

                if (typeof content === 'string') {
                    onSrtChange(content);
                }
            };

            reader.readAsText(file);
        };

        input.click();
    }, [onSrtChange]);

    const handleDownload = useCallback(() => {
        if (!srtContent.trim()) {
            return;
        }

        const safeDesignName = sanitizeFileNamePart(designName) || 'design';
        const safePageName = sanitizeFileNamePart(pageName) || 'page';
        const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
        const downloadUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');

        anchor.href = downloadUrl;
        anchor.download = `${safeDesignName}_${safePageName}.srt`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(downloadUrl);
    }, [srtContent, designName, pageName]);

    return (
        <Container>
            <Flex gap="3">
                <Button
                    icon={<IconSparkles />}
                    variant="primary"
                    onClick={onGenerate}
                    isDisabled={isGenerating || isDisabled}
                    isFullWidth
                >
                    {isGenerating
                        ? translate('editor.transcript.generate-button.generating')
                        : translate('editor.transcript.generate-button')}
                </Button>
                <Dropdown
                    trigger={({ isOpen, ...triggerProps }) => (
                        <Button
                            icon={<IconMore />}
                            variant="secondary"
                            isDisabled={isGenerating}
                            title=""
                            {...triggerProps}
                        />
                    )}
                >
                    <Dropdown.Item onClick={handleUpload} icon={<IconUpload />}>
                        {translate('editor.transcript.dropdown.upload')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleDownload} icon={<IconDownload />} isDisabled={!srtContent.trim()}>
                        {translate('editor.transcript.dropdown.download')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleClearAll} icon={<IconClear />} isDisabled={!srtContent.trim()}>
                        {translate('editor.transcript.dropdown.clear')}
                    </Dropdown.Item>
                </Dropdown>
            </Flex>
            <Box paddingTop="3" paddingBottom="3">
                <Copy>{cues.length === 0 && translate('editor.transcript.description')}</Copy>
            </Box>
            <CueList>
                {cues.length === 0 ? (
                    <Textarea
                        placeholder={translate('editor.transcript.textarea.placeholder')}
                        value=""
                        isDisabled={isDisabled}
                    />
                ) : (
                    cues.map((cue, i) => (
                        <React.Fragment key={cue.id}>
                            <TranscriptCueItem
                                cue={cue}
                                index={i + 1}
                                frameRate={frameRate}
                                isEditing={editingCueId === cue.id}
                                onStartEdit={() => setEditingCueId(cue.id)}
                                onEndEdit={() => setEditingCueId((current) => (current === cue.id ? null : current))}
                                onUpdate={handleCueUpdate}
                                onDelete={handleCueDelete}
                                onInsertAfter={handleCueInsertAfter}
                            />
                        </React.Fragment>
                    ))
                )}
            </CueList>
        </Container>
    );
};

const Copy = styled.p`
    ${textStyles.uiTextS};
    margin-bottom: 0;
`;

const CueList = styled.div`
    padding: ${token.spacing2} 0;
`;

const Container = styled.div`
    margin-bottom: ${token.spacing3};
`;

export default TranscriptForm;
